import React, { useEffect, useState, useRef } from 'react';
import { Send, Users, CheckCircle, AlertTriangle, Search, X, FileText, Mail } from 'lucide-react';
import { useApi } from './DashboardLayout';
import TipTapEditor from '../components/TipTapEditor';
import { FeedbackBanner, PlaceholderBar } from '../components/SharedUI';

const RECIPIENT_TYPES = [
  { value: 'all_users', label: 'Alle Anbieter', icon: Users },
  { value: 'certified', label: 'Anbieter mit zertifizierten Angeboten', icon: CheckCircle },
  { value: 'selected', label: 'Ausgewählte Anbieter', icon: Search },
];

const COMPOSER_PLACEHOLDERS = ['portal_name', 'empfaenger_name', 'empfaenger_email', 'verein', 'club_id', 'betreff', 'nachricht', 'initialpasswort', 'login_url'];

export default function MailComposerPage() {
  const { apiFetch } = useApi();
  const editorRef = useRef(null);
  const [recipientType, setRecipientType] = useState('');
  const [providers, setProviders] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);

  useEffect(() => { loadTemplates(); }, []);

  useEffect(() => {
    if (recipientType === 'selected') loadProviders();
    else if (recipientType === 'all_users') loadRecipientCount('/api/mail/recipients/providers');
    else if (recipientType === 'certified') loadRecipientCount('/api/mail/recipients/certified');
    else setRecipientCount(null);
  }, [recipientType]);

  const loadTemplates = async () => {
    const r = await apiFetch('/api/settings/mail-templates?category=general');
    if (!r) return;
    try { const d = await r.json(); if (Array.isArray(d)) setTemplates(d); } catch {}
  };

  const loadProviders = async () => {
    const r = await apiFetch('/api/mail/recipients/providers');
    if (!r) return;
    try { const d = await r.json(); if (Array.isArray(d)) setProviders(d); } catch {}
  };

  const loadRecipientCount = async (url) => {
    const r = await apiFetch(url);
    if (!r) return;
    try { const d = await r.json(); if (Array.isArray(d)) setRecipientCount(d.length); } catch {}
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 8000); };

  const handleTemplateChange = async (key) => {
    setSelectedTemplate(key);
    if (!key) { setSubject(''); setBody(''); return; }
    const r = await apiFetch(`/api/settings/mail-templates/${key}`);
    if (!r) return;
    try {
      const d = await r.json();
      if (d) { setSubject(d.subject || ''); setBody(d.body || ''); }
    } catch {}
  };

  const toggleProvider = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedIds(filteredProviders.map(p => p.id));
  const deselectAll = () => setSelectedIds([]);

  const filteredProviders = providers.filter(p => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (p.verein || '').toLowerCase().includes(s) || (p.email || '').toLowerCase().includes(s) || (p.contact_person || '').toLowerCase().includes(s);
  });

  const handleSend = async () => {
    if (!recipientType) { showFb('error', 'Bitte Empfängergruppe wählen.'); return; }
    if (recipientType === 'selected' && selectedIds.length === 0) { showFb('error', 'Bitte mindestens einen Anbieter auswählen.'); return; }
    if (!subject && !useTemplate) { showFb('error', 'Bitte Betreff eingeben.'); return; }
    if (!body && !useTemplate) { showFb('error', 'Bitte Inhalt eingeben.'); return; }

    const count = recipientType === 'selected' ? selectedIds.length : recipientCount;
    if (!window.confirm(`E-Mail an ${count || '?'} Empfänger senden?`)) return;

    setSending(true);
    try {
      const payload = {
        recipient_type: recipientType,
        subject,
        body,
        template_key: useTemplate ? selectedTemplate : null,
        selected_provider_ids: recipientType === 'selected' ? selectedIds : [],
      };
      const r = await apiFetch('/api/mail/send', { method: 'POST', body: JSON.stringify(payload) });
      if (!r) { setSending(false); return; }
      const text = await r.text();
      const d = JSON.parse(text);
      if (d?.status) {
        showFb('success', d.message);
        setSubject(''); setBody(''); setSelectedTemplate(''); setRecipientType(''); setSelectedIds([]);
      } else showFb('error', d?.detail || 'Fehler beim Senden.');
    } catch (e) { showFb('error', e.message); }
    setSending(false);
  };

  const insertPlaceholder = (p) => {
    const editor = editorRef.current;
    if (editor) {
      editor.chain().focus().insertContent(`{{${p}}}`).run();
    }
  };

  const activePlaceholders = useTemplate && selectedTemplate
    ? (templates.find(t => t.key === selectedTemplate)?.placeholders || COMPOSER_PLACEHOLDERS)
    : COMPOSER_PLACEHOLDERS;

  return (
    <div data-testid="mail-composer-page">
      <div className="page-header">
        <h2>E-Mail verfassen</h2>
        <p>E-Mail an Anbieter senden</p>
      </div>

      {feedback && <FeedbackBanner feedback={feedback} testId="composer-feedback" />}

      {/* Recipient Selection */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Empfänger</h4>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: recipientType === 'selected' ? 16 : 0 }}>
            {RECIPIENT_TYPES.map(rt => (
              <button key={rt.value} type="button" onClick={() => { setRecipientType(rt.value); setSelectedIds([]); }}
                data-testid={`recipient-type-${rt.value}`}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
                  border: recipientType === rt.value ? '2px solid var(--primary)' : '1px solid var(--border)',
                  background: recipientType === rt.value ? 'rgba(204,0,0,0.05)' : 'var(--page-bg)',
                  color: recipientType === rt.value ? 'var(--primary)' : 'var(--text-secondary)',
                }}>
                <rt.icon size={15} /> {rt.label}
                {recipientType === rt.value && recipientCount !== null && rt.value !== 'selected' && (
                  <span style={{ background: 'var(--primary)', color: '#fff', padding: '1px 8px', borderRadius: 12, fontSize: 11 }}>{recipientCount}</span>
                )}
              </button>
            ))}
          </div>

          {recipientType === 'selected' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                  <input className="form-input" placeholder="Anbieter suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: 32, fontSize: 13 }} data-testid="provider-search-input" />
                </div>
                <button type="button" onClick={selectAll} style={{ fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Alle</button>
                <button type="button" onClick={deselectAll} style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}>Keine</button>
                {selectedIds.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{selectedIds.length} ausgewählt</span>}
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 4 }} data-testid="provider-select-list">
                {filteredProviders.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', cursor: 'pointer', borderRadius: 6, fontSize: 13, background: selectedIds.includes(p.id) ? 'rgba(204,0,0,0.04)' : 'transparent' }}
                    data-testid={`provider-checkbox-${p.id}`}>
                    <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleProvider(p.id)} style={{ accentColor: 'var(--primary)' }} />
                    <span style={{ fontWeight: 600 }}>{p.verein}</span>
                    <span style={{ color: '#9ca3af', fontSize: 12 }}>{p.email}</span>
                  </label>
                ))}
                {filteredProviders.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Keine Anbieter gefunden</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Template or Free Text Toggle */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12, marginBottom: useTemplate ? 12 : 0 }}>
            <button type="button" onClick={() => { setUseTemplate(false); setSelectedTemplate(''); }}
              data-testid="mode-freetext"
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                border: !useTemplate ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: !useTemplate ? 'rgba(204,0,0,0.05)' : 'var(--page-bg)',
                color: !useTemplate ? 'var(--primary)' : 'var(--text-secondary)',
              }}>
              <Mail size={15} /> Freier Text
            </button>
            <button type="button" onClick={() => setUseTemplate(true)}
              data-testid="mode-template"
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                border: useTemplate ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: useTemplate ? 'rgba(204,0,0,0.05)' : 'var(--page-bg)',
                color: useTemplate ? 'var(--primary)' : 'var(--text-secondary)',
              }}>
              <FileText size={15} /> Vorlage verwenden
            </button>
          </div>

          {useTemplate && (
            <select className="form-input" value={selectedTemplate} onChange={e => handleTemplateChange(e.target.value)}
              style={{ maxWidth: 400 }} data-testid="template-select">
              <option value="">-- Vorlage wählen --</option>
              {templates.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Placeholders */}
      <PlaceholderBar placeholders={activePlaceholders} onInsert={insertPlaceholder} />

      {/* Subject */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <label className="smtp-label">Betreff</label>
          <input className="form-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="E-Mail-Betreff" data-testid="composer-subject" />
        </div>
      </div>

      {/* Body */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <label className="smtp-label" style={{ marginBottom: 8, display: 'block' }}>Inhalt</label>
          <TipTapEditor content={body} onChange={setBody} placeholder="E-Mail-Inhalt eingeben..." editorRef={editorRef} />
        </div>
      </div>

      <button className="btn btn-primary" onClick={handleSend} disabled={sending || !recipientType} data-testid="composer-send-btn">
        <Send size={14} /> {sending ? 'Wird gesendet...' : 'E-Mail senden'}
      </button>
    </div>
  );
}
